import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useKpros } from '../../context/KprosContext';
import { useTheme } from '../../context/ThemeContext';
import { Send, Plus, Trash2, MessageSquare, ChevronLeft, Loader2, Paperclip, X, FileText, Image, Clock, Edit3, Check } from 'lucide-react';
import { parseNavLinks } from './KprosNavLink';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

const UNNA = { fontFamily: 'Unna, serif' };

function renderMessageContent(content) {
  if (!content) return null;

  const hasNavLinks = /\[\[nav:/.test(content);

  if (hasNavLinks) {
    const navLinkPattern = /\[\[nav:([^\]|]+)\|([^\]]+)\]\]/g;
    const navLinks = [];
    let match;
    while ((match = navLinkPattern.exec(content)) !== null) {
      navLinks.push({ path: match[1], label: match[2] });
    }
    const cleanedContent = content.replace(navLinkPattern, '').trim();
    const html = DOMPurify.sanitize(marked.parse(cleanedContent));

    return (
      <div>
        <div className="prose prose-sm dark:prose-invert max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0" dangerouslySetInnerHTML={{ __html: html }} />
        {navLinks.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {navLinks.map((link, i) => (
              <React.Fragment key={i}>
                {parseNavLinks(`[[nav:${link.path}|${link.label}]]`).filter(p => typeof p !== 'string')}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    );
  }

  const html = DOMPurify.sanitize(marked.parse(content));
  return <div className="prose prose-sm dark:prose-invert max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0" dangerouslySetInnerHTML={{ __html: html }} />;
}

export default function KprosChat() {
  const {
    messages, chatLoading, sendChat, startNewConversation,
    currentConversationId, conversations, conversationsLoading,
    fetchConversations, selectConversation, renameConversation, deleteConversation,
  } = useKpros();
  const { theme } = useTheme();
  const location = useLocation();
  const [input, setInput] = useState('');
  const [attachedFile, setAttachedFile] = useState(null);
  const [view, setView] = useState('chat');
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  const handleSend = async () => {
    if ((!input.trim() && !attachedFile) || chatLoading) return;
    const msg = input || (attachedFile ? `[Attached: ${attachedFile.name}]` : '');
    const file = attachedFile;
    setInput('');
    setAttachedFile(null);
    await sendChat(msg, location.pathname, file);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) setAttachedFile(file);
    e.target.value = '';
  };

  const isImageFile = (file) => file?.type?.startsWith('image/');

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSelectConversation = (id) => {
    selectConversation(id);
    setView('chat');
  };

  const handleNewConversation = () => {
    startNewConversation();
    setView('chat');
  };

  // ── History View ──
  if (view === 'history') {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <button onClick={() => setView('chat')} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-500">
            <ChevronLeft size={14} />
          </button>
          <span className="text-sm font-medium text-gray-600 dark:text-gray-300 flex-1" style={UNNA}>History</span>
          <button
            onClick={handleNewConversation}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-blue-500"
            title="New"
          >
            <Plus size={14} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {conversationsLoading ? (
            <div className="flex justify-center py-6">
              <Loader2 size={14} className="animate-spin text-gray-400" />
            </div>
          ) : conversations.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">No conversations yet</p>
          ) : (
            conversations.map(c => (
              <div
                key={c.id}
                className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-sm border-b border-gray-100 dark:border-gray-700 transition-colors ${
                  currentConversationId === c.id
                    ? 'bg-blue-50 dark:bg-blue-900/20'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-750'
                }`}
                onClick={() => editingId !== c.id && handleSelectConversation(c.id)}
              >
                <MessageSquare size={12} className="text-gray-400 flex-shrink-0" />
                {editingId === c.id ? (
                  <input
                    type="text"
                    value={editTitle}
                    onChange={e => setEditTitle(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') { renameConversation(c.id, editTitle.trim()); setEditingId(null); }
                      if (e.key === 'Escape') setEditingId(null);
                    }}
                    onBlur={() => { if (editTitle.trim()) renameConversation(c.id, editTitle.trim()); setEditingId(null); }}
                    className="flex-1 text-sm px-1 py-0.5 rounded border border-blue-400 bg-white dark:bg-gray-700 dark:text-white focus:outline-none"
                    autoFocus
                    onClick={e => e.stopPropagation()}
                  />
                ) : (
                  <span className="flex-1 truncate text-gray-700 dark:text-gray-300">{c.title}</span>
                )}
                <div className="flex items-center gap-0.5 flex-shrink-0">
                  {editingId === c.id ? (
                    <button
                      onClick={(e) => { e.stopPropagation(); renameConversation(c.id, editTitle.trim()); setEditingId(null); }}
                      className="p-0.5 hover:text-green-500 text-gray-400"
                      title="Save"
                    >
                      <Check size={12} />
                    </button>
                  ) : (
                    <button
                      onClick={(e) => { e.stopPropagation(); setEditingId(c.id); setEditTitle(c.title); }}
                      className="p-0.5 hover:text-blue-500 text-gray-400"
                      title="Rename"
                    >
                      <Edit3 size={12} />
                    </button>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteConversation(c.id); }}
                    className="p-0.5 hover:text-red-500 text-gray-400"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  // ── Chat View ──
  const isEmptyChat = messages.length === 0 && !chatLoading;
  const hasPastConversations = conversations.length > 0;

  return (
    <div className="flex flex-col h-full">
      {/* Header — icons only, compact */}
      <div className="flex items-center gap-1.5 px-3 py-1.5 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        {hasPastConversations && (
          <button
            onClick={() => setView('history')}
            className="px-1.5 py-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-500 relative flex items-center gap-1 text-xs whitespace-nowrap"
            title="History"
          >
            <span>History</span>
            <Clock size={12} />
            <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-blue-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center" style={{ fontFamily: 'system-ui' }}>
              {conversations.length}
            </span>
          </button>
        )}
        <span className="flex-1" />
        {currentConversationId && (
          <button
            onClick={handleNewConversation}
            className="px-1.5 py-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-blue-500 flex items-center gap-1 text-xs whitespace-nowrap"
            title="New"
          >
            <span>New Ask</span>
            <Plus size={12} />
          </button>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {isEmptyChat && (
          <div className="py-3">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2 text-center" style={UNNA}>Ask anything about JPRO</p>
            <div className="space-y-1">
              {['How do I create good Studio prompts?', 'What is the AIT feature?', 'Help me with data extraction'].map((s, i) => (
                <button
                  key={i}
                  onClick={() => setInput(s)}
                  className="block w-full text-sm text-left px-3 py-1.5 rounded bg-gray-50 dark:bg-gray-750 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
            {/* Quick access to recent conversations */}
            {hasPastConversations && (
              <div className="mt-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                <p className="text-xs uppercase tracking-wider text-gray-400 mb-1 px-1" style={UNNA}>Recent</p>
                {conversations.slice(0, 3).map(c => (
                  <button
                    key={c.id}
                    onClick={() => handleSelectConversation(c.id)}
                    className="flex items-center gap-1.5 w-full px-2 py-1 text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-750 rounded transition-colors"
                  >
                    <MessageSquare size={10} className="text-gray-300 dark:text-gray-600 flex-shrink-0" />
                    <span className="truncate">{c.title}</span>
                  </button>
                ))}
                {conversations.length > 3 && (
                  <button
                    onClick={() => setView('history')}
                    className="text-xs text-blue-500 hover:text-blue-600 px-2 py-0.5"
                  >
                    View all ({conversations.length})
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] px-3 py-2 rounded-lg text-sm ${
              msg.role === 'user'
                ? 'bg-blue-500 text-white'
                : theme === 'dark'
                  ? 'bg-gray-750 text-gray-200 border border-gray-600'
                  : 'bg-gray-100 text-gray-800'
            }`}>
              {msg.role === 'assistant' ? renderMessageContent(msg.content) : (
                <>
                  {msg.fileName && (
                    <div className="flex items-center gap-1 mb-1 opacity-80">
                      <Paperclip size={10} />
                      <span className="truncate text-xs">{msg.fileName}</span>
                    </div>
                  )}
                  {msg.content}
                </>
              )}
              {msg.citations && msg.citations.length > 0 && (
                <div className="mt-1.5 pt-1.5 border-t border-gray-200 dark:border-gray-600">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Sources:</p>
                  {msg.citations.map((c, j) => (
                    <a key={j} href={c.url} target="_blank" rel="noopener noreferrer"
                      className="block text-xs text-blue-500 hover:underline truncate">
                      {c.title || c.url}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        {chatLoading && (
          <div className="flex justify-start">
            <div className={`px-3 py-2 rounded-lg ${theme === 'dark' ? 'bg-gray-750' : 'bg-gray-100'}`}>
              <Loader2 size={14} className="animate-spin text-blue-500" />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="flex-shrink-0 p-2 border-t border-gray-200 dark:border-gray-700">
        {attachedFile && (
          <div className="flex items-center gap-1.5 mb-1.5 px-2 py-1 rounded bg-gray-50 dark:bg-gray-700 text-xs text-gray-600 dark:text-gray-300">
            {isImageFile(attachedFile) ? <Image size={13} className="text-blue-500 flex-shrink-0" /> : <FileText size={13} className="text-gray-400 flex-shrink-0" />}
            <span className="flex-1 truncate">{attachedFile.name}</span>
            <button onClick={() => setAttachedFile(null)} className="p-0.5 hover:text-red-500 flex-shrink-0">
              <X size={11} />
            </button>
          </div>
        )}
        <div className="flex items-end gap-1.5">
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept="image/*,.pdf,.doc,.docx,.xlsx,.xls,.csv,.txt,.pptx"
            onChange={handleFileSelect}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors flex-shrink-0"
            title="Attach"
          >
            <Paperclip size={14} />
          </button>
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask Kpros..."
            rows={1}
            className="flex-1 resize-none px-2 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <button
            onClick={handleSend}
            disabled={(!input.trim() && !attachedFile) || chatLoading}
            className="p-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
          >
            <Send size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}
